import { useState, useEffect } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Map, Lock, CheckCircle2, Gift, Gamepad2, Zap, Gem, Flame, Rocket, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import cyberBg from "@/assets/game/cyber-trending.png";

interface MilestoneConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  glowColor: string;
  requirement: string;
}

const MILESTONES: MilestoneConfig[] = [
  {
    id: "balance_100_reward",
    title: "$100 Balance — Claim $100!",
    description: "Reach $100 in your account and claim a one-time $100 cash reward!",
    icon: Gift,
    color: "from-purple-500 to-emerald-500",
    glowColor: "purple-400",
    requirement: "Reach $100 balance",
  },
  {
    id: "balance_100",
    title: "$100 Balance — FLOW Invaders",
    description: "Unlock the FLOW Invaders mini-game! 2 lives per day, earn virtual cash.",
    icon: Gamepad2,
    color: "from-pink-500 to-cyan-500",
    glowColor: "cyan-400",
    requirement: "Reach $100 balance to play",
  },
  {
    id: "x2_multiplier",
    title: "x2 Multiplier — $300 Earned",
    description: "Your game score doubles! Plus claim a $5 daily bonus every day.",
    icon: Zap,
    color: "from-yellow-400 to-orange-500",
    glowColor: "yellow-400",
    requirement: "Earn $300 total",
  },
  {
    id: "coming_soon_1",
    title: "Coming Soon",
    description: "Something exciting is being prepared... Stay tuned!",
    icon: Gem,
    color: "from-purple-400 to-pink-500",
    glowColor: "purple-400",
    requirement: "TBD",
  },
  {
    id: "coming_soon_2",
    title: "Coming Soon",
    description: "Another surprise is on the way. Details will be revealed soon!",
    icon: Flame,
    color: "from-orange-400 to-red-500",
    glowColor: "orange-400",
    requirement: "TBD",
  },
  {
    id: "airdrop",
    title: "Real EMON AirDrop",
    description: "A real EMON token AirDrop for top players. Details TBA.",
    icon: Rocket,
    color: "from-yellow-300 to-amber-500",
    glowColor: "yellow-300",
    requirement: "Details TBA",
  },
];

export default function GameRoadmap() {
  const { session, player, loading, signOut } = useGameAuth();
  const [claimedMilestones, setClaimedMilestones] = useState<Set<string>>(new Set());
  const [claiming, setClaiming] = useState(false);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);

  useEffect(() => {
    if (player) {
      fetchClaims();
    }
  }, [player]);

  const fetchClaims = async () => {
    if (!player) return;
    const { data } = await supabase
      .from("game_milestone_claims")
      .select("milestone_id")
      .eq("player_id", player.id);
    if (data) {
      const ids = new Set(data.map((d: any) => d.milestone_id));
      setClaimedMilestones(ids);
      // Check if daily bonus already claimed today
      const todayKey = `daily_x2_bonus_${new Date().toISOString().split("T")[0]}`;
      setDailyBonusClaimed(ids.has(todayKey));
    }
  };

  const claimReward = async (milestoneId: string) => {
    if (!player || claiming) return;
    setClaiming(true);
    try {
      const { error } = await supabase.rpc("claim_game_milestone_reward", {
        p_player_id: player.id,
        p_milestone_id: milestoneId,
      });
      if (error) throw error;
      toast.success(`+$100 reward claimed! 🎉`);
      setClaimedMilestones((prev) => new Set([...prev, milestoneId]));
      window.location.reload();
    } catch (err: any) {
      toast.error(err.message || "Failed to claim reward");
    } finally {
      setClaiming(false);
    }
  };

  const claimDailyBonus = async () => {
    if (!player || claimingDaily) return;
    setClaimingDaily(true);
    try {
      const { error } = await supabase.rpc("claim_daily_x2_bonus", {
        p_player_id: player.id,
      });
      if (error) throw error;
      toast.success(`+$5 daily bonus claimed! 🎉`);
      setDailyBonusClaimed(true);
      window.location.reload();
    } catch (err: any) {
      toast.error(err.message || "Failed to claim daily bonus");
    } finally {
      setClaimingDaily(false);
    }
  };

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  const balance = Number(player.virtual_balance);
  const totalEarned = Number(player.total_earned);

  const getMilestoneStatus = (id: string): "locked" | "unlocked" | "claimed" => {
    switch (id) {
      case "balance_100_reward": return claimedMilestones.has(id) ? "claimed" : balance >= 100 ? "unlocked" : "locked";
      case "balance_100": return balance >= 100 ? "unlocked" : "locked";
      case "x2_multiplier": return totalEarned >= 300 ? "unlocked" : "locked";
      case "coming_soon_1": return "locked";
      case "coming_soon_2": return "locked";
      case "airdrop": return "locked";
      default: return "locked";
    }
  };

  const getProgress = (id: string): number => {
    switch (id) {
      case "balance_100_reward": return Math.min(100, (balance / 100) * 100);
      case "balance_100": return Math.min(100, (balance / 100) * 100);
      case "x2_multiplier": return Math.min(100, (totalEarned / 300) * 100);
      case "coming_soon_1": return 0;
      case "coming_soon_2": return 0;
      case "airdrop": return 0;
      default: return 0;
    }
  };

  const getProgressLabel = (id: string): string => {
    switch (id) {
      case "balance_100_reward": return `$${balance.toFixed(2)} / $100`;
      case "balance_100": return `$${balance.toFixed(2)} / $100`;
      case "x2_multiplier": return `$${totalEarned.toFixed(2)} / $300`;
      case "coming_soon_1": return "TBD";
      case "coming_soon_2": return "TBD";
      case "airdrop": return "TBA";
      default: return "";
    }
  };

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="cyber-page-bg max-w-2xl mx-auto px-4 py-8 space-y-6 relative"
        style={{ ['--cyber-bg' as string]: `url(${cyberBg})` }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -z-10 w-60 h-60 bg-purple-500/10 rounded-full blur-[100px]" />

        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mx-auto shadow-xl shadow-purple-500/30">
            <Map className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-game-gradient">Roadmap</h1>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Trade, sell, and grow your balance to unlock <span className="text-cyan-400 font-bold">epic rewards</span>!
          </p>
        </div>

        <div className="relative space-y-4">
          <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-white/5" />

          {MILESTONES.map((milestone, index) => {
            const status = getMilestoneStatus(milestone.id);
            const progress = getProgress(milestone.id);
            const progressLabel = getProgressLabel(milestone.id);
            const isUnlocked = status === "unlocked" || status === "claimed";

            return (
              <div key={milestone.id} className="relative pl-14">
                <div
                  className={`absolute left-3.5 top-5 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 ${
                    isUnlocked
                      ? "bg-purple-500 border-purple-400 shadow-lg shadow-purple-400/40"
                      : "bg-gray-800 border-gray-600"
                  }`}
                >
                  {isUnlocked ? (
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  ) : (
                    <span className="text-[10px] text-gray-500 font-bold">{index + 1}</span>
                  )}
                </div>

                <Card
                  className={`border transition-all ${
                    isUnlocked
                      ? "border-purple-500/30 bg-purple-500/[0.03] shadow-lg shadow-purple-500/5"
                      : "border-white/5 bg-gray-900/80"
                  }`}
                >
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${milestone.color} flex items-center justify-center shrink-0 ${
                            isUnlocked ? "shadow-lg" : "opacity-50"
                          }`}
                        >
                          <milestone.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className={`font-bold text-sm ${isUnlocked ? "text-white" : "text-gray-400"}`}>
                            {milestone.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{milestone.description}</p>
                        </div>
                      </div>
                      {isUnlocked ? (
                        <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded-full ${
                          status === "claimed" ? "bg-cyan-500/15 text-cyan-400" : "bg-purple-500/15 text-cyan-400"
                        }`}>
                          {status === "claimed" ? "Claimed ✓" : "Unlocked"}
                        </span>
                      ) : (
                        <Lock className="w-4 h-4 text-gray-600 shrink-0 mt-1" />
                      )}
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-500">{milestone.requirement}</span>
                        <span className={isUnlocked ? "text-cyan-400 font-bold" : "text-gray-500"}>
                          {progressLabel}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isUnlocked
                              ? `bg-gradient-to-r ${milestone.color}`
                              : "bg-gray-600"
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Action buttons */}
                    {milestone.id === "balance_100_reward" && status === "unlocked" && (
                      <Button
                        size="sm"
                        onClick={() => claimReward("balance_100_reward")}
                        disabled={claiming}
                        className="w-full font-bold bg-gradient-to-r from-purple-500 to-cyan-500 text-black hover:from-purple-500 hover:to-cyan-300 border-0"
                      >
                        {claiming ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Claiming...</> : "🎁 Claim $100 Reward"}
                      </Button>
                    )}
                    {milestone.id === "balance_100" && status === "unlocked" && (
                      <Link to="/game/minigame" className="block">
                        <Button
                          size="sm"
                          className="w-full font-bold bg-gradient-to-r from-pink-500 to-cyan-500 text-black hover:from-pink-500 hover:to-blue-400 border-0"
                        >
                          🎮 Play FLOW Invaders
                        </Button>
                      </Link>
                    )}
                    {milestone.id === "x2_multiplier" && status === "unlocked" && (
                      <Button
                        size="sm"
                        onClick={claimDailyBonus}
                        disabled={claimingDaily || dailyBonusClaimed}
                        className="w-full font-bold bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:from-yellow-400 hover:to-orange-400 border-0"
                      >
                        {dailyBonusClaimed
                          ? "✅ Daily $5 Bonus Claimed"
                          : claimingDaily
                            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Claiming...</>
                            : "⚡ Claim Daily $5 Bonus"
                        }
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </GameLayout>
  );
}
